/**
 * The left-hand navigation menu — one definition, two consumers.
 *
 * This list used to live inside Sidebar.jsx. It moved here when users got the
 * ability to order the menu themselves: the editor in My Account has to render
 * exactly the same items the sidebar does, and a second copy of a 150-item
 * list would have drifted apart within a release.
 *
 * Ordering is a per-user preference (see utils/navLayout.js). Nothing here
 * knows about that — buildMenuGroups() always returns the DEFAULT order, and
 * applyNavLayout() rearranges it.
 */
import { pathToPermCode } from './pagePermissions';
import {
  LayoutDashboard, Users, FileText, ClipboardList, TestTube2,
  DollarSign, Settings, BarChart3, Package,
  Building2, FileCheck, Brain,
  Search,
  FlaskConical, Microscope, ShieldCheck, Boxes, Truck,
  CalendarDays, MessageSquare, Bell, CreditCard, FileBarChart,
  Stethoscope, Activity, Heart,
  FolderOpen, Globe,
  Monitor, ShoppingCart, Smartphone,
  MapPin, Layers, Wrench, AlertTriangle, Target, TrendingUp,
  ClipboardCheck, UserCheck, Trophy, Calculator, FileUp, CheckCircle, Send, User,
  FileWarning,
  Phone, Briefcase, Mail, Award, RotateCcw, ScanBarcode,
} from 'lucide-react';

// The unnamed group at the very top of the sidebar (Dashboard, Patients, …)
// has no title, so it needs a stable key of its own to be orderable.
export const TOP_GROUP_KEY = '__top__';

/** Stable identity for a group, used as the key in a saved layout. */
export const groupKey = (group) => group?.title || TOP_GROUP_KEY;

/**
 * The default menu, in the order the product ships it.
 *
 * `userRole` and `isGlobal` gate two entries; `hostname` gates Corporate
 * Billing (a platform-owner screen, not a lab one).
 */
export function buildMenuGroups({
  userRole = '', isGlobal = false, hostname = null, isCollector = false,
} = {}) {
  const role = (userRole || '').toLowerCase();
  const host = hostname == null ? (typeof window !== 'undefined' ? window.location.hostname : '') : hostname;
  // For a mobile collector this IS the job, so it ships at the very top
  // rather than inside Field Operations — which for a client login is a
  // collapsed group holding exactly one item they have to go find. An
  // administrator who is not a collector still finds it under Field
  // Operations, where it belongs among the other field screens.
  const collectorItem = {
    title: 'Mobile Collector', icon: Smartphone, path: '/collect',
    requiresCollector: true,
  };
  return [
    {
      title: null,
      items: [
        ...(isCollector ? [collectorItem] : []),
        { title: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
        { title: 'Ops Hub', icon: Activity, path: '/ops' },
        { title: 'Executive Agents', icon: Brain, path: '/executive' },
        { title: 'User Activity', icon: Activity, path: '/user-activity' },
        { title: 'Patients', icon: Users, path: '/patients' },
        { title: 'Assessments', icon: ClipboardCheck, path: '/assessments' },
        { title: 'Clients', icon: Building2, path: '/clients' },
        { title: 'Providers', icon: Stethoscope, path: '/providers' },
        { title: 'Requisitions', icon: FileCheck, path: '/requisitions' },
        { title: 'Orders', icon: ClipboardList, path: '/orders' },
        { title: 'Specimens', icon: TestTube2, path: '/specimens' },
        { title: 'Results', icon: FileText, path: '/results' },
      ]
    },
    {
      title: 'LAB',
      items: [
        { title: 'Accessioning', icon: Activity, path: '/accessioning' },
        { title: 'Accessioning Manager', icon: Activity, path: '/accessioning/manager' },
        { title: 'Microbiology', icon: Microscope, path: '/microbiology' },
        { title: 'Reference / Send-Outs', icon: Send, path: '/sendouts' },
        { title: 'QC Manager', icon: FlaskConical, path: '/qc' },
        { title: 'Proficiency Testing', icon: Award, path: '/proficiency' },
        { title: 'Lab Maintenance', icon: Wrench, path: '/lab-maintenance' },
        { title: 'Lab Portal', icon: Microscope, path: '/lab' },
        { title: 'Order Receiving', icon: Package, path: '/lab/order-received' },
        { title: 'Specimen Check-In', icon: ScanBarcode, path: '/specimen-checkin' },
        { title: 'Audit Logs', icon: FileText, path: '/audit-logs' },
      ]
    },
    {
      title: 'BILLING / RCM',
      items: [
        { title: 'Billing Manager', icon: Briefcase, path: '/billing/manager' },
        { title: 'My Biller Dashboard', icon: DollarSign, path: '/billing/biller' },
        { title: 'RCM Dashboard', icon: DollarSign, path: '/rcm' },
        { title: 'Eligibility Check', icon: ShieldCheck, path: '/rcm/eligibility' },
        { title: 'Settlements', icon: FileText, path: '/rcm/settlements' },
        { title: 'Submit Claims', icon: Send, path: '/rcm/submit-claims' },
        { title: 'Paper Claims', icon: FileText, path: '/rcm/paper-claims' },
        { title: 'Payers', icon: Building2, path: '/rcm/payers' },
        { title: 'Payer Portals', icon: Building2, path: '/payer-portals' },
        { title: 'Fee Schedules', icon: DollarSign, path: '/rcm/fee-schedules' },
        { title: 'Work Queues', icon: ClipboardCheck, path: '/rcm/work-queues' },
        { title: 'AI EOB Manager', icon: FileCheck, path: '/rcm/eob' },
        { title: 'Claims', icon: CreditCard, path: '/claims' },
        { title: 'Denials & Appeals', icon: AlertTriangle, path: '/rcm/denials' },
        { title: 'Appeals Worklist', icon: FileWarning, path: '/rcm/appeals' },
        { title: 'Action Items', icon: ClipboardCheck, path: '/rcm/actions' },
        { title: 'ERA Posting', icon: FileUp, path: '/rcm/era-posting' },
        { title: 'Payment Posting', icon: DollarSign, path: '/rcm/payment-posting' },
        { title: 'Refunds & Takebacks', icon: RotateCcw, path: '/rcm/recoupments' },

        { title: 'Invoices', icon: FileBarChart, path: '/invoices' },
        { title: 'Statements', icon: FileText, path: '/statements' },
        { title: 'Mail Queue', icon: Mail, path: '/mail-queue' },
        { title: 'Cost Estimator', icon: Calculator, path: '/rcm/cost-estimator' },
        { title: 'Payer Intelligence', icon: Target, path: '/rcm/payer-intelligence' },
        { title: 'Denial Prediction', icon: Brain, path: '/rcm/risk-assessment' },
        { title: 'Credentialing', icon: UserCheck, path: '/rcm/credentialing' },
        { title: 'Provider Analytics', icon: TrendingUp, path: '/rcm/provider-analytics' },
        { title: 'Smart Tasks', icon: Brain, path: '/rcm/smart-tasks' },
        { title: 'Deadline Calendar', icon: CalendarDays, path: '/rcm/deadlines' },
        { title: 'Revenue Forecast', icon: TrendingUp, path: '/rcm/revenue-forecast' },
        { title: 'ML Prediction', icon: TrendingUp, path: '/rcm/ml-prediction' },
        { title: 'Auto-Verify Rules', icon: CheckCircle, path: '/rcm/auto-verify' },
        { title: 'Extraction Templates', icon: FileText, path: '/rcm/templates' },
        { title: 'Bulk Appeals', icon: AlertTriangle, path: '/rcm/bulk-appeals' },
        { title: 'Patient Financials', icon: User, path: '/rcm/patient-financials' },
        { title: 'Client Financials', icon: Building2, path: '/rcm/client-financials' },
        { title: 'Team Leaderboard', icon: Trophy, path: '/rcm/leaderboard' },
      ]
    },
    {
      title: 'SCHEDULING',
      items: [
        { title: 'Appointments', icon: CalendarDays, path: '/schedule' },
      ]
    },
    {
      title: 'FIELD OPERATIONS',
      items: [
        // Dispatch Center, Field Schedule, Visit Logs, AND Recurring
        // Schedules are all tabs inside the unified Dispatch & Pickups page
        // now (Live · Queue · Recurring · History · Analytics). Their old
        // routes redirect into the right tab.
        // Only when it is NOT already at the top — see `collectorItem` above.
        // Listed twice would give a collector two links to the same screen
        // and two rows to reorder in My Account.
        ...(isCollector ? [] : [{
          title: 'Mobile Collector', icon: Smartphone, path: '/collect',
          requiresCollector: true,
        }]),
        { title: 'Dispatch & Pickups', icon: Bell, path: '/dispatch-pickups' },
        { title: 'Collector Performance', icon: Smartphone, path: '/collectors/performance' },
        { title: 'Live Map', icon: Globe, path: '/live-map' },
        { title: 'Technician App', icon: Smartphone, path: '/tech-app' },
        { title: 'Vehicles', icon: Truck, path: '/vehicles' },
        { title: 'Fleet Telematics', icon: MapPin, path: '/fleet-telematics' },
        { title: 'Fleet Analytics', icon: TrendingUp, path: '/fleet-analytics' },
        { title: 'Phlebotomy Log', icon: FlaskConical, path: '/phlebotomy-log' },
        { title: 'EOD Report', icon: ClipboardCheck, path: '/eod-report' },
        { title: 'EOD Reports — Review', icon: ClipboardCheck, path: '/eod-reports' },
      ]
    },
    {
      title: 'INVENTORY',
      items: [
        { title: 'Inventory Hub', icon: Boxes, path: '/inventory/hub' },
        { title: 'Inventory Audit', icon: ClipboardList, path: '/inventory/audit' },
        { title: 'Supply Requests', icon: Truck, path: '/inventory/supply-requests' },
        { title: 'Consumption Station', icon: Bell, path: '/consumption' },
        { title: 'Inventory Settings', icon: Layers, path: '/inventory/settings' },
        { title: 'Purchasing', icon: ShoppingCart, path: '/purchasing' },
        { title: 'Reorder Alerts', icon: Bell, path: '/inventory/alerts' },
        // Warehouses is now a tab inside Inventory Settings — legacy /warehouse entry removed
        { title: 'Fulfillment', icon: Package, path: '/fulfillment' },
        // Executive Agents moved up to the top group (just below Dashboard)
      ]
    },
    {
      title: 'E-COMMERCE',
      items: [
        { title: 'Store Front', icon: ShoppingCart, path: '/store' },
        { title: 'Admin Fulfillment', icon: Truck, path: '/admin/fulfillment' },
        { title: 'Admin Shipments', icon: Truck, path: '/admin/shipments' },
        { title: 'Subscriptions', icon: Heart, path: '/admin/subscriptions' },
      ]
    },
    {
      title: 'SALES CRM',
      items: [
        { title: 'Sales Pipeline', icon: Heart, path: '/sales' },
        // Commission periods, rep payments, reconciliation and 1099
        // reporting. Setting reps up stays in Settings; this is the work.
        { title: 'Commissions', icon: DollarSign, path: '/commissions' },
      ]
    },
    {
      title: 'AI & ANALYTICS',
      items: [
        { title: 'AI Dashboard', icon: Brain, path: '/ai-dashboard' },
        { title: 'Operations', icon: BarChart3, path: '/operations' },
        { title: 'Report Builder', icon: FileBarChart, path: '/report-builder' },
      ]
    },
    {
      title: 'COMMUNICATIONS',
      items: [
        { title: 'Department Chat', icon: MessageSquare, path: '/chat' },
        { title: 'Tasks', icon: ClipboardCheck, path: '/tasks' },
        { title: 'Messaging', icon: MessageSquare, path: '/messaging' },
        { title: 'Communication Center', icon: MessageSquare, path: '/communication-center' },
        { title: 'Client Service', icon: Users, path: '/client-service' },
        { title: 'Call Logs', icon: Phone, path: '/call-logs' },
        { title: 'Fax Inbox', icon: Phone, path: '/fax' },
        { title: 'Documents', icon: FolderOpen, path: '/documents' },
        { title: 'Google Workspace', icon: Globe, path: '/google-workspace' },
      ]
    },
    {
      title: 'MY HEALTH',
      items: [
        { title: 'Patient Portal', icon: Heart, path: '/patient' },
        { title: 'Store', icon: ShoppingCart, path: '/store' },
      ]
    },
    {
      title: 'WEBSITE',
      items: [
        { title: 'Home', icon: Globe, path: '/home' },
        { title: 'Test Catalog', icon: TestTube2, path: '/test-catalog' },
        { title: 'Contact Us', icon: MessageSquare, path: '/contact' },
      ]
    },
    {
      title: 'ADMIN',
      items: [
        { title: 'PDF Management', icon: FileText, path: '/pdf-editor' },
        { title: 'CMS / Website', icon: Globe, path: '/cms' },
        { title: 'Settings', icon: Settings, path: '/settings' },
        // Exclusion screening. Lives here rather than under a tab because it
        // covers providers, staff, vendors and ordering practices alike, and
        // a page reachable only by typing its URL is a page nobody uses.
        { title: 'OIG Screening', icon: ShieldCheck, path: '/compliance/oig' },
        // CORPORATE BILLING — the platform's own books.
        //
        // This was gated on `host === 'xpiva.com'`, which is not a control.
        // Anybody could change it in devtools, and the API never checked
        // the hostname at all, so hiding the menu protected nothing.
        // role_page_permissions also grants the page to role 'admin' —
        // which every laboratory's own administrator carries — so that
        // string was the only thing in the way.
        //
        // The gate is now the same capability the API enforces
        // (require_platform_owner: is_global AND superadmin), so what a
        // person can SEE and what they can DO cannot drift apart. The
        // hostname is kept only to avoid rendering the platform's own
        // books on a customer's branded domain, which is presentation,
        // not security.
        ...((isGlobal && role === 'superadmin'
             && (host === 'xpiva.com' || host === 'localhost'))
            ? [{ title: 'Corporate Billing', icon: Building2, path: '/saas-billing' }] : []),
        { title: 'Kiosk Mode', icon: Monitor, path: '/kiosk' },
        // A DISPLAY, not an input station — the entry next to it takes
        // input, this one only shows. Opening it on a workstation asks for
        // a pairing code, because a board authenticates as a device.
        { title: 'Wall Display', icon: Monitor, path: '/wallboard' },
        { title: 'Help Center', icon: Search, path: '/help' },
        // System Health is restricted to superadmin OR users with the
        // is_global flag — anyone else won't even see the link.
        ...((role === 'superadmin' || !!isGlobal)
          ? [{ title: 'System Health', icon: Activity, path: '/admin/system-health' }]
          : []),
      ]
    },
  ];
}


/**
 * Narrow the menu to the pages a role may view.
 *
 * One copy of this rule on purpose: the sidebar and the ordering editor in
 * My Account both call it, and an editor that listed pages the sidebar hides
 * would invite people to arrange rows they will never see.
 *
 * `permissions` is the map from GET /page-permissions/my-permissions. Admins
 * and superadmins bypass it, as they do everywhere else.
 */
export function filterMenuByPermissions(
  groups, { userRole = '', permissions = null, isCollector = false } = {},
) {
  const role = (userRole || '').toLowerCase();
  const isAdmin = role === 'admin' || role === 'superadmin';
  // Profile-gated items are filtered even for an administrator: an admin who
  // is not a collector has no reason to carry the link, and the page itself
  // still lets them in if they go there.
  const collectorOk = (it) => !it.requiresCollector || isCollector || isAdmin;
  if (!permissions || isAdmin) {
    return groups
      .map(g => ({ ...g, items: g.items.filter(collectorOk) }))
      .filter(g => g.items.length > 0);
  }
  const canView = (it) => {
    if (it.requiresCollector) return isCollector;
    const code = pathToPermCode(it.path);
    if (!code) return true;              // not a permissioned page
    const perm = permissions[code];
    if (!perm) return false;             // unknown to the matrix — hide it
    return !!perm.can_view;
  };
  return groups
    .map(g => ({ ...g, items: g.items.filter(canView) }))
    .filter(g => g.items.length > 0);
}
